<template>
  <div class="staff-credits-page" data-testid="staff-credits-page">
    <page-header
      :title="t('Configurações IA')"
      icon="pi pi-bolt"
    />

    <Tabs :value="activeTab" @update:value="handleTabChange">
      <TabList>
        <Tab value="visao-geral" data-testid="staff-credits-tab-visao-geral">
          {{ t('Visão geral') }}
        </Tab>
        <Tab value="consumo" data-testid="staff-credits-tab-consumo">
          {{ t('Consumo') }}
        </Tab>
        <Tab value="pacotes" data-testid="staff-credits-tab-pacotes">
          {{ t('Pacotes') }}
        </Tab>
        <Tab value="calculadora" data-testid="staff-credits-tab-calculadora">
          {{ t('Calculadora') }}
        </Tab>
        <Tab value="custo-provedor" data-testid="staff-credits-tab-custo-provedor">
          {{ t('Custo do provedor (DeepSeek)') }}
        </Tab>
        <Tab value="configuracoes" data-testid="staff-credits-tab-configuracoes">
          {{ t('Configurações') }}
        </Tab>
        <Tab value="auditoria" data-testid="staff-credits-tab-auditoria">
          {{ t('Auditoria') }}
        </Tab>
      </TabList>

      <TabPanels>
        <!-- Tab 1: Visão geral -->
        <TabPanel value="visao-geral">
          <div data-testid="staff-credits-tab-panel-visao-geral">
            <staff-credits-kpi-cards
              v-if="overviewDados"
              :dados="overviewDados"
              data-testid="staff-credits-kpi-cards"
            />

            <div
              v-if="isFull"
              class="agent-panels-section"
              data-testid="staff-credits-agent-panels"
            >
              <AgentHealthPanel />
              <AgentStatsPanel />
            </div>
          </div>
        </TabPanel>

        <!-- Tab 2: Consumo -->
        <TabPanel value="consumo">
          <div data-testid="staff-credits-tab-panel-consumo">
            <div class="tab-actions">
              <Button
                :label="t('Adicionar créditos')"
                icon="pi pi-plus"
                severity="primary"
                data-testid="staff-credits-topup-open"
                @click="handleOpenTopup"
              />
              <Button
                v-if="isFull"
                :label="t('Ajuste manual')"
                icon="pi pi-sliders-h"
                severity="secondary"
                data-testid="staff-credits-adjust-open"
                @click="handleOpenAdjust"
              />
            </div>

            <staff-credits-table
              :rows="ledgerRows"
              data-testid="staff-credits-table"
              @refresh="fetchLedger()"
            />
          </div>
        </TabPanel>

        <!-- Tab 3: Pacotes -->
        <TabPanel value="pacotes">
          <div data-testid="staff-credits-tab-panel-pacotes">
            <div
              v-if="isFull"
              data-testid="credit-packs-section"
            >
              <div class="packs-section-header">
                <h3 class="packs-section-title">{{ t('Pacotes de Créditos') }}</h3>
                <div class="packs-section-actions">
                  <!-- Manual reconciliation of pending credit-pack purchases (#27
                       follow-up). Gate is the ROUTE itself (Full-only) — never
                       can_reconcile, which is derived from the SUBSCRIPTION's
                       provider and does not apply to this franchise-scoped door. -->
                  <Button
                    :label="t('Sincronizar compras de créditos')"
                    icon="pi pi-sync"
                    severity="secondary"
                    data-testid="staff-credits-sync-open"
                    @click="handleOpenSync"
                  />
                  <Button
                    :label="t('Novo pacote')"
                    icon="pi pi-plus"
                    severity="primary"
                    data-testid="credit-packs-create-button"
                    @click="handleOpenCreatePack"
                  />
                </div>
              </div>
              <credit-packs-table
                :rows="packRows"
                data-testid="credit-packs-table-section"
                @edit="handleEditPack"
                @toggle-active="handleTogglePackActive"
                @refresh="fetchPacks()"
              />
              <credit-purchases-table
                :rows="purchaseRows"
                data-testid="credit-purchases-table-section"
                @refresh="fetchPurchases()"
              />
            </div>
          </div>
        </TabPanel>

        <!-- Tab 4: Calculadora -->
        <TabPanel value="calculadora">
          <div data-testid="staff-credits-tab-panel-calculadora">
            <credit-pricing-calculator
              v-if="isFull"
              :dados="overviewDados"
              data-testid="staff-credit-pricing-calculator"
            />
          </div>
        </TabPanel>

        <!-- Tab 5: Custo do provedor (DeepSeek) -->
        <TabPanel value="custo-provedor">
          <div data-testid="staff-credits-tab-panel-custo-provedor">
            <provider-cost-form v-if="isFull" />
          </div>
        </TabPanel>

        <!-- Tab 6: Configurações -->
        <TabPanel value="configuracoes">
          <div data-testid="staff-credits-tab-panel-configuracoes">
            <div v-if="isFull">
              <configuracoes-ia-settings-form
                class="settings-form-section"
                data-testid="config-ia-settings-section"
              />
            </div>
          </div>
        </TabPanel>

        <!-- Tab 7: Auditoria -->
        <TabPanel value="auditoria">
          <div data-testid="staff-credits-tab-panel-auditoria">
            <AgentAuditPanel v-if="isFull" />
          </div>
        </TabPanel>
      </TabPanels>
    </Tabs>

    <!-- Modals are registered globally in ModalManager.vue — no local instances here -->
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { eventBus } from '../../../eventBus';
import { useCreditWallet } from '../../../composables/useCreditWallet';
import { useCreditPacks } from '../../../composables/useCreditPacks';
import { useCreditPurchases } from '../../../composables/useCreditPurchases';
import { log } from '../../../utils/DebugLogger';
import type { AiCredits_AdminOverviewResponseDados } from '../../../contracts/AiCredits_AdminOverviewResponse';
import type { AiCredits_LedgerRow } from '../../../contracts/AiCredits_LedgerRow';
import type { AiCredits_PackRow } from '../../../contracts/AiCredits_PackRow';
import PageHeader from '../../../ui/PageHeader.vue';
import permissionsService from '../../../services/permissionsService';
import AgentHealthPanel from '../../assistant/AgentHealthPanel.vue';
import AgentStatsPanel from '../../assistant/AgentStatsPanel.vue';
import AgentAuditPanel from '../../assistant/AgentAuditPanel.vue';
import ConfiguracoesIaSettingsForm from './ConfiguracoesIaSettingsForm.vue';
import CreditPricingCalculator from './CreditPricingCalculator.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const isFull = computed<boolean>(() => permissionsService.isUserInGroups(['Full']));

const { fetchAdminOverview, fetchLedger: fetchLedgerComposable } = useCreditWallet();
const { packs: packRows, fetchPacks, setActive } = useCreditPacks();
const { purchases: purchaseRows, fetchPurchases } = useCreditPurchases();

const overviewDados = ref<AiCredits_AdminOverviewResponseDados | null>(null);
const ledgerRows = ref<AiCredits_LedgerRow[]>([]);

// ── Tab shell — ORDER TABLE, decided 2026-08-14 (docs/ai-credits-console-refactor-plan.md §4) ──

const TAB_SLUGS = [
  'visao-geral',
  'consumo',
  'pacotes',
  'calculadora',
  'custo-provedor',
  'configuracoes',
  'auditoria',
] as const;
type TabSlug = (typeof TAB_SLUGS)[number];
const DEFAULT_TAB: TabSlug = 'visao-geral';

const activeTab = ref<TabSlug>(DEFAULT_TAB);

function tabFromHash(hash: string | undefined): TabSlug {
  const slug = hash?.replace(/^#/, '');
  return (TAB_SLUGS as readonly string[]).includes(slug ?? '') ? (slug as TabSlug) : DEFAULT_TAB;
}

function handleTabChange(value: string | number): void {
  activeTab.value = value as TabSlug;
  router.replace({ hash: `#${value}` }).catch(() => {});
}

function handleOpenTopup(): void {
  eventBus.emit('modal.staff-credits-topup.open');
}

function handleOpenAdjust(): void {
  eventBus.emit('modal.credit-manual-adjustment.open');
}

function handleOpenSync(): void {
  eventBus.emit('modal.staff-credits-sync.open');
}

function handleOpenCreatePack(): void {
  eventBus.emit('modal.creditPack.open');
}

function handleEditPack(pack: AiCredits_PackRow): void {
  eventBus.emit('modal.creditPack.open', { pack });
}

async function handleTogglePackActive(pack: AiCredits_PackRow): Promise<void> {
  try {
    const newActive = pack.active === 1 ? 0 : 1;
    await setActive(pack.id, newActive as 0 | 1);
    eventBus.emit('globalMessage.success', t('Pacote atualizado'));
    await fetchPacks();
  } catch (err) {
    log('StaffCreditsPage', 'handleTogglePackActive', 'Erro ao atualizar pack', err);
    eventBus.emit('globalMessage.error', t('Erro ao atualizar pacote'));
  }
}

async function loadOverview(): Promise<void> {
  try {
    const resp = await fetchAdminOverview() as { dados: AiCredits_AdminOverviewResponseDados };
    overviewDados.value = resp.dados;
  } catch (err) {
    log('StaffCreditsPage', 'loadOverview', 'Erro ao carregar overview', err);
  }
}

async function fetchLedger(): Promise<void> {
  try {
    const resp = await fetchLedgerComposable() as { dados: { rows: AiCredits_LedgerRow[] } };
    ledgerRows.value = resp.dados?.rows ?? [];
  } catch (err) {
    log('StaffCreditsPage', 'fetchLedger', 'Erro ao carregar ledger', err);
  }
}

async function handleRefresh(): Promise<void> {
  log('StaffCreditsPage', 'handleRefresh', 'Refreshing ledger after topup');
  await fetchLedger();
}

async function handlePackRefresh(): Promise<void> {
  await fetchPacks();
}

async function loadPurchases(): Promise<void> {
  try {
    await fetchPurchases();
  } catch (err) {
    log('StaffCreditsPage', 'loadPurchases', 'Erro ao carregar compras', err);
  }
}

onMounted(async () => {
  log('StaffCreditsPage', 'mounted', 'Component mounted');
  activeTab.value = tabFromHash(route.hash);
  eventBus.on('staff-credits.refresh', handleRefresh);
  eventBus.on('creditPack.refresh', handlePackRefresh);
  const loads: Promise<void>[] = [loadOverview(), fetchLedger()];
  if (isFull.value) {
    loads.push(fetchPacks());
    loads.push(loadPurchases());
  }
  await Promise.all(loads);
});

// Vue Router REUSES this component instance when only the hash changes, so
// onMounted does not re-fire — without this watch, browser back/forward between
// tabs would leave the shell showing the previous tab while the URL says
// otherwise. Same reason (and same comment) as LgpdRegistrosPage.vue:70.
watch(
  () => route.hash,
  (hash) => {
    const next = tabFromHash(hash);
    if (next !== activeTab.value) {
      activeTab.value = next;
    }
  },
);

onBeforeUnmount(() => {
  log('StaffCreditsPage', 'beforeUnmount', 'Cleaning up listeners');
  eventBus.off('staff-credits.refresh', handleRefresh);
  eventBus.off('creditPack.refresh', handlePackRefresh);
});
</script>

<style scoped>
.staff-credits-page {
  padding: 24px;
}

.tab-actions {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.packs-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.packs-section-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.packs-section-actions {
  display: flex;
  gap: 8px;
}

.settings-form-section {
  margin-top: 0;
}

.agent-panels-section {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
</style>
